/**
 * Hook for managing the Bridge Manager lifecycle
 * 
 * Creates and destroys the BridgeManager based on component lifecycle.
 * Also handles showing the install prompt overlay.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { BridgeManager, destroyBridgeManager } from '../bridges'
import type { StoreUrls } from '../bridges/types'

interface UseBridgeManagerOptions {
  storeUrls?: StoreUrls
  promptEnabled?: boolean
}

interface UseBridgeManagerResult {
  bridgeManager: BridgeManager | null
  showInstallPrompt: boolean
  promptFeature: string
  closeInstallPrompt: () => void
}

export function useBridgeManager(options: UseBridgeManagerOptions = {}): UseBridgeManagerResult {
  const { storeUrls, promptEnabled = true } = options
  
  const managerRef = useRef<BridgeManager | null>(null)
  const [showInstallPrompt, setShowInstallPrompt] = useState(false)
  const [promptFeature, setPromptFeature] = useState('')
  
  // Handle install prompt display
  const handleShowInstallPrompt = useCallback((feature: string) => {
    setPromptFeature(feature)
    setShowInstallPrompt(true)
  }, [])
  
  const closeInstallPrompt = useCallback(() => {
    setShowInstallPrompt(false)
  }, [])
  
  // Initialize BridgeManager
  useEffect(() => {
    // Create the bridge manager
    managerRef.current = new BridgeManager({
      onShowInstallPrompt: handleShowInstallPrompt,
      storeUrls: storeUrls || { android: null, ios: null },
      promptEnabled
    })
    
    // Cleanup on unmount
    return () => {
      destroyBridgeManager()
      managerRef.current = null
    }
  }, [handleShowInstallPrompt, promptEnabled]) // Don't include storeUrls to avoid re-creating
  
  // Update store URLs when they change
  useEffect(() => {
    if (managerRef.current && storeUrls) {
      managerRef.current.updateStoreUrls(storeUrls)
    }
  }, [storeUrls])
  
  return {
    bridgeManager: managerRef.current,
    showInstallPrompt,
    promptFeature,
    closeInstallPrompt
  }
}

